import { defaultPlatformBrands, bootstrapPlatformBrandId } from '@kilat/config-seed';
import type { PlatformBrand } from '@kilat/shared';

/**
 * Build-time DEFAULT platform brand — the seeded `kilat` brand from
 * `@kilat/config-seed`, completed with placeholder timestamps so it satisfies
 * the full `PlatformBrand` shape.
 *
 * Used by the static prerender (ADR-0024 slice 2b part B): during build-time
 * page snapshotting the SPA renders against THIS default brand **synchronously**,
 * so prerender never issues a Firestore read — and so never trips the #70
 * brand-gate error surface. At runtime the live SPA still resolves the brand
 * from `platformBrands/{brandId}` and rehydrates; this constant is only the
 * first-paint stand-in, extending the CLAUDE.md "index.html carries build-time
 * defaults so first paint is correct" pattern to full-page prerender.
 */

// Placeholder timestamp — never rendered (marketing shows no brand createdAt/
// updatedAt), present only to satisfy the schema shape. Not a stored value, so
// rule #4 ("no new Date for stored values") does not apply.
const PRERENDER_TS: PlatformBrand['createdAt'] = { seconds: 0, nanoseconds: 0 };

const seed = defaultPlatformBrands.find((b) => b.id === bootstrapPlatformBrandId);
if (!seed) {
  throw new Error(`config-seed: bootstrap platform brand "${bootstrapPlatformBrandId}" missing`);
}

export const DEFAULT_PLATFORM_BRAND: PlatformBrand = {
  ...seed,
  createdAt: PRERENDER_TS,
  updatedAt: PRERENDER_TS,
};

/**
 * True only inside the build-time prerender browser (the flag is set by
 * `scripts/prerender.mjs` via `addInitScript` BEFORE the SPA boots). Never set
 * at runtime, so live visitors keep the async brand read + #70 gate unchanged.
 */
export function isPrerender(): boolean {
  return typeof window !== 'undefined' && window.__KILAT_PRERENDER__ === true;
}
